import React, { useState, useEffect } from "react";
import classNames from "classnames";
import { Typography } from "../../Typography/Typography";
import "./Tab.scss";

/**
 * Interface for Tab sub-components.
 *
 * @description Defines the sub-components available on the Tabs component.
 */
interface TabSubComponents {
  Tab: React.FC<TabProps>;
}

/**
 * Props for the Tabs container component.
 *
 * @description The Tabs component is a container for Tab components,
 * providing a consistent layout for tab navigation.
 *
 * @property {React.ReactNode} children - Tab components to render inside the container. Required.
 * @property {string} [className] - Additional CSS class names to apply to the container.
 *
 * @example
 * // Tabs container with multiple tabs
 * <Tabs>
 *   <Tabs.Tab title="Overview" name="tabs" checked={active === 'overview'} onClick={() => setActive('overview')} />
 *   <Tabs.Tab title="Details" name="tabs" checked={active === 'details'} onClick={() => setActive('details')} />
 * </Tabs>
 */
interface Props {
  children: React.ReactNode;
  className?: string;
}

/**
 * Props for the Tab component.
 *
 * @description The Tab component is a single tab item that can be used within a Tabs container.
 * It renders as a radio button styled as a tab with optional count display.
 *
 * @property {string} title - The text displayed on the tab. Required.
 * @property {string} name - The name attribute for the radio input (groups tabs together). Required.
 * @property {number} [count] - Optional count number displayed on 2depth tabs.
 *   Numbers less than 10 are zero-padded (e.g., 5 becomes "05").
 * @property {string} [id] - HTML id attribute for the radio input.
 * @property {boolean} [checked=false] - Whether the tab is currently selected.
 * @property {boolean} [disabled=false] - Whether the tab is disabled and non-interactive.
 * @property {string} [className] - Additional CSS class names to apply to the tab text.
 * @property {"1depth" | "2depth"} [type="1depth"] - The depth level of the tab.
 *   - "1depth": Primary tab styling
 *   - "2depth": Secondary tab styling with optional count display
 * @property {() => void} [onClick] - Click event handler. Called when the tab is clicked.
 * @property {(event: React.ChangeEvent<HTMLInputElement>, type: any) => void} [onChange] - Change event handler.
 *   Called when the tab selection changes. Receives the event and the tab type.
 * @property {string} [testId] - Data-testid attribute for the tab container.
 * @property {string} [radioTestId] - Data-testid attribute for the radio input.
 * @property {string} [buttonTestid] - Data-testid attribute for the button element.
 * @property {string} [toolTip] - Tooltip text displayed on hover.
 *
 * @example
 * // Primary tab
 * <Tabs.Tab
 *   title="Dashboard"
 *   name="main-tabs"
 *   checked={activeTab === 'dashboard'}
 *   onClick={() => setActiveTab('dashboard')}
 * />
 *
 * @example
 * // Secondary tab with count
 * <Tabs.Tab
 *   title="Messages"
 *   name="sub-tabs"
 *   type="2depth"
 *   count={5}
 *   checked={activeTab === 'messages'}
 *   onClick={() => setActiveTab('messages')}
 * />
 */
interface TabProps {
  title: string;
  name: string;
  count?: number;
  id?: string;
  checked?: boolean;
  disabled?: boolean;
  className?: string;
  type?: "1depth" | "2depth";
  onClick?: () => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, type: any) => void;
  testId?: string;
  radioTestId?: string;
  buttonTestid?: string;
  toolTip?: string;
}

/**
 * A container component for organizing Tab components.
 *
 * @description Tabs provides a consistent layout container for tab navigation.
 * Use it with Tabs.Tab sub-component to create tabbed interfaces.
 *
 * Features:
 * - Consistent container styling
 * - Flexible children rendering
 * - Custom className support
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A div container with tab styling
 *
 * @see Props for all available properties
 * @see Tab for individual tab component
 */
const Tabs: React.FC<Props> & TabSubComponents = (props) => {
  const { children, className } = props;
  return (
    <div className={classNames("tab--container", className)}>{children}</div>
  );
};

/**
 * A single tab item component for tab navigation.
 *
 * @description Tab renders a radio button styled as a tab with integrated label support.
 * It supports two depth levels (1depth and 2depth) with different visual styling.
 * The 2depth variant can display an optional count number.
 *
 * Features:
 * - Two depth levels with distinct styling
 * - Optional count display for 2depth tabs
 * - Integrated Typography component for text
 * - Disabled state support
 * - Radio button behavior for exclusive selection
 *
 * @param {TabProps} props - The component props
 * @returns {JSX.Element} A labeled tab element with radio input
 *
 * @see TabProps for all available properties
 */
const Tab: React.FC<TabProps> = (props) => {
  const {
    title,
    name,
    id,
    checked = false,
    disabled = false,
    type = "1depth",
    onChange,
    onClick,
    count,
    testId,
    radioTestId,
    className,
    buttonTestid,
    toolTip,
  } = props;

  useEffect(() => {
    setSelected(checked);
  }, [checked]);

  const [selected, setSelected] = useState<boolean>(false);

  const handleOnChange = (event: any) => {
    if (onChange) {
      onChange(event, type);
    }
  };

  const handleOnClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const wrapClass = classNames("tab", {
    [`tab--${type}`]: !!type,
    [`tab--${type}--selected`]: !!selected,
  });
  return (
    <label className={wrapClass} data-testid={testId}>
      <input
        data-testid={radioTestId}
        id={id}
        type="radio"
        checked={selected}
        disabled={disabled}
        onClick={handleOnClick}
        onChange={handleOnChange}
        name={name}
      ></input>
      <button
        data-testid={buttonTestid}
        className="tab__content"
        onClick={handleOnChange}
        disabled={disabled}
      >
        <Typography component="span" variant="body2" className={className}>
          {title}
        </Typography>
        {type === "2depth" && count && (
          <Typography
            component="span"
            variant="body2"
            className="tab--2depth__count"
          >
            {count < 10 ? "0" + count : count}
          </Typography>
        )}
      </button>
    </label>
  );
};

Tabs.Tab = Tab;

export default Tabs;
